<template>
  <div class="player-container">
    <div class="player-wrapper">
      <video-player
        ref="player"
        :key="playerKey"
        :start-time="startTime"
        :video-id="videoId"
        @timeupdate="handleTimeUpdate"
        @ready="onPlayerReady"
      />
    </div>
    
    <countdown-timer
      v-if="countdownActive"
      :type="countdownType"
      :progress="countdownProgress"
      :seconds="remainingCountdownSeconds"
    />

    <!-- Blocks direct interaction with the YouTube controls -->
    <div class="player-overlay" @click="$emit('overlay-click')"></div>

    <div class="center-play-button" @click="$emit('overlay-click')" v-show="showPlayButton">
      <div class="play-circle play-circle--overlay">
        <i class="fas fa-play"></i>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex';
import VideoPlayer from "./Index";
import CountdownTimer from "../ui/CountdownTimer.vue";

export default {
  name: 'VideoPlayerSection',
  props: {
    showPlayButton: { type: Boolean, default: false }
  },
  components: {
    VideoPlayer,
    CountdownTimer
  },
  data() {
    return {
      currentSubtitle: null,
      lastTime: 0
    }
  },
  computed: {
    ...mapState('player', ['videoId', 'playerKey', 'startTime', 'playerState']),
    ...mapState('recognition', [
      'recognizedText',
      'countdownActive',
      'countdownType',
      'countdownProgress'
    ]),
    ...mapState('settings', ['showOverlay']),
    
    ...mapGetters({
      remainingCountdownSeconds: 'recognition/remainingCountdownSeconds'
    })
  },
  methods: {
    togglePlayback() {
      if (this.$refs.player && this.$refs.player.player) {
        this.$refs.player.player.getPlayerState().then(state => {
          if (state === 1) { // Playing
            this.$refs.player.pause();
          } else {
            this.$refs.player.resume();
          }
        });
      }
    },
    
    onPlayerReady() {
      this.$store.dispatch('player/initializePlayer', this.$refs.player);
      
      if (this.$refs.player) {
        if (this.$refs.player.setupSubtitleChangeCallback) {
          this.$refs.player.setupSubtitleChangeCallback(subtitle => {
            if (subtitle) {
              this.currentSubtitle = subtitle;
              this.$emit('subtitle-change', subtitle);
            }
          });
        }
      }
    },
    
    handleTimeUpdate(time) {
      this.$emit('timeupdate', time);
      
      this.processSubtitles(time);
    },
    
    processSubtitles(time) {
      // Throttle to 0.2s steps.
      if (Math.abs(time - this.lastTime) < 0.2) return;
      this.lastTime = time;
      
      if (this.$refs.player && this.$refs.player.getSubtitleAtTime) {
        const subtitle = this.$refs.player.getSubtitleAtTime(time);
        
        if (subtitle && (!this.currentSubtitle || 
            subtitle.text !== this.currentSubtitle.text || 
            subtitle.startTime !== this.currentSubtitle.startTime)) {
          this.currentSubtitle = subtitle;
          this.$emit('subtitle-change', subtitle);
        }
      }
    },
    
    seekTo(time) {
      if (this.$refs.player && this.$refs.player.player) {
        this.$refs.player.player.seekTo(time);
      }
    },
    
    getCurrentTime() {
      return new Promise((resolve, reject) => {
        if (this.$refs.player && this.$refs.player.player) {
          this.$refs.player.player.getCurrentTime().then(resolve).catch(reject);
        } else {
          reject(new Error('Player not available'));
        }
      });
    },
    
    getPlayerState() {
      return new Promise((resolve, reject) => {
        if (this.$refs.player && this.$refs.player.player) {
          this.$refs.player.player.getPlayerState().then(resolve).catch(reject);
        } else {
          reject(new Error('Player not available'));
        }
      });
    },
    
    pause() {
      if (this.$refs.player) {
        this.$refs.player.pause();
      }
    },
    
    resume() {
      if (this.$refs.player) {
        this.$refs.player.resume();
      }
    },
    
    playAt(time, paused = false) {
      if (this.$refs.player) {
        this.$refs.player.playAt(time, paused);
      }
    }
  }
}
</script>

<style scoped>
.player-container {
  position: relative;
  background: var(--chrome);
  overflow: hidden;
  display: flex;
  flex-direction: column;
  width: 100%;
  aspect-ratio: 16 / 9;
  flex-shrink: 0;
}


.player-wrapper {
  position: relative;
  width: 100%;
  height: 100%;
  flex: 1;
  overflow: hidden;
}

.player-wrapper >>> iframe {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  border: 0;
}

.player-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 10;
  cursor: default;
  background: transparent;
  pointer-events: all;
}

.center-play-button {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 20;
  cursor: pointer;
  opacity: 0.9;
  transition: opacity 0.3s;
}

.center-play-button:hover {
  opacity: 1;
}

.play-circle--overlay {
  width: 64px;
  height: 64px;
  background: var(--accent);
  border-radius: var(--r-pill);
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.5);
  transition: transform 0.2s, background-color 0.2s;
}

.play-circle--overlay:hover {
  transform: scale(1.06);
  background: var(--accent-hover);
}

.play-circle--overlay i {
  font-size: 24px;
  color: var(--accent-ink);
  transform: translateX(2px);
}

/* Short viewports: cap the player so the drill card stays above the fold. */
@media (max-height: 600px) {
  .player-container {
    max-height: 42vh;
  }
}
</style>
